<template>
  <FormPage
    title="Водители"
    description="Список всех водителей в системе"
  >
    <template #controls>
      <button
        class="btn btn-primary"
        type="button"
        @click="router.push({ name: 'DriverCreate' })"
      >+ Создать водителя</button>
    </template>

    <FormCard>
      <FormGrid :cols="3">
        <FormField label="Общий поиск">
          <input
            v-model.trim="filters.query"
            type="text"
            placeholder="Имя, Фамилия, Отчество, Номер телефона, ИНН, Водительское удостоверение"
            @input="debouncedFetchFirstPage"
          />
        </FormField>

        <FormField label="Поиск по имени">
          <input
            v-model.trim="filters.queryFirstName"
            type="text"
            placeholder="Имя"
            @input="debouncedFetchFirstPage"
          />
        </FormField>

        <FormField label="Поиск по фамилии">
          <input
            v-model.trim="filters.queryLastName"
            type="text"
            placeholder="Фамилия"
            @input="debouncedFetchFirstPage"
          />
        </FormField>

        <FormField label="Поиск по отчеству">
          <input
            v-model.trim="filters.querySurname"
            type="text"
            placeholder="Отчество"
            @input="debouncedFetchFirstPage"
          />
        </FormField>

        <FormField label="Поиск по номеру телефона">
          <input
            v-model.trim="filters.queryPhoneNumber"
            type="text"
            placeholder="Номер телефона"
            @input="debouncedFetchFirstPage"
          />
        </FormField>

        <FormField label="Поиск по ИНН">
          <input
            v-model.trim="filters.queryINN"
            type="text"
            placeholder="ИНН"
            @input="debouncedFetchFirstPage"
          />
        </FormField>

        <FormField label="Поиск по водительскому удостоверению">
          <input
            v-model.trim="filters.queryLicense"
            type="text"
            placeholder="Номер водительского удостоверения"
            @input="debouncedFetchFirstPage"
          />
        </FormField>

        <FormField label="Статус проверки ИНН">
          <select v-model="filters.INNStatus" @change="debouncedFetchFirstPage">
            <option v-for="opt in statusINNOptions" :key="opt.value" :value="opt.value">
              {{ opt.text }}
            </option>
          </select>
        </FormField>

        <FormField label="Статус проверки паспорта">
          <select v-model="filters.passportStatus" @change="debouncedFetchFirstPage">
            <option v-for="opt in statusPassportOptions" :key="opt.value" :value="opt.value">
              {{ opt.text }}
            </option>
          </select>
        </FormField>

        <div class="filters-actions">
          <button class="btn" type="button" @click="resetFilters">Сбросить</button>
        </div>
      </FormGrid>
    </FormCard>

    <FormCard>
      <my-pagination
        :currentPage="currentPage"
        :totalPages="totalPages"
        @pageChanged="pageChanged"
      />

      <my-loader v-if="isLoading" />

      <div v-else class="content">
        <table v-if="drivers.length" class="driver-table">
          <colgroup>
            <col class="col-fio" />
            <col class="col-phone-number" />
            <col class="col-INN" />
            <col class="col-INN-check-status" />
            <col class="col-driver-license-number" />
            <col class="col-black-list" />
            <col class="col-client-black-list" />
            <col class="col-passport-check-status" />
          </colgroup>
          <thead>
            <tr>
              <th>ФИО</th>
              <th>Номер телефона</th>
              <th>ИНН</th>
              <th>Статус проверки ИНН</th>
              <th>Водительское удостоверение</th>
              <th>Черный список</th>
              <th>Клиентский ЧС</th>
              <th>Статус проверки паспорта</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="dr in drivers"
              :key="dr.id"
              class="row-card"
              @click="$router.push(`/drivers/${dr.id}`)"
            >
              <td :title="[dr.first_name, dr.last_name, dr.surname].filter(Boolean).join(' ')" class="row-fio">{{ [dr.first_name, dr.last_name, dr.surname].filter(Boolean).join(' ') || '—' }}</td>
              <td :title="dr.phone">{{ dr.phone || '—' }}</td>
              <td :title="dr.inn">{{ dr.inn || '—' }}</td>
              <td>
                <span :class="['status-badge', dr.security_info?.dadata_is_end ? 'st-verified' : 'st-nverified']">
                  {{ dr.security_info?.dadata_is_end ? 'Проверено' : 'Не проверено' }}
                </span>
              </td>
              <td :title="dr.license">{{ dr.license || '—' }}</td>
              <td>{{ dr.security_info?.black_list ? '📛' : '—' }}</td>
              <td>{{ dr.security_info?.black_list_client ? '📛' : '—' }}</td>
              <td>
                <span :class="['status-badge', dr.security_info?.nalog_is_end ? 'st-verified' : 'st-nverified']">
                  {{ dr.security_info?.nalog_is_end ? 'Проверено' : 'Не проверено' }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>

        <div v-else class="state-block">
          Пользователей по выбранным фильтрам нет.
        </div>
      </div>
    </FormCard>
  </FormPage>
</template>

<script>
import { useStore } from 'vuex'
import { ref, computed, onMounted, reactive } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useDebounceFn } from '@vueuse/core'
import FormPage from '@/components/formKit/FormPage.vue'
import FormCard from '@/components/formKit/FormCard.vue'
import FormGrid from '@/components/formKit/FormGrid.vue'
import FormField from '@/components/formKit/FormField.vue'

export default {
  name: 'DriverList',
  components: {
    FormPage,
    FormCard,
    FormGrid,
    FormField,
  },
  setup() {
    const store = useStore()
    const router = useRouter()
    const route = useRoute()

    const drivers = computed(() => store.state.apiStorage.drivers.items || [])
    const isLoading = computed(() => store.state.apiStorage.drivers.statusLoading)
    const totalPages = computed(() => store.state.apiStorage.drivers.totalPages)
    const currentPage = computed(() => store.state.apiStorage.drivers.currentPage)

    const loadData = (page) => {
      store.dispatch('apiStorage/getFilteredList', { model: 'drivers', params: { page, ...filterParams.value } })
    }

    const filters = reactive({
      query: route.query.query || '',
      queryFirstName: route.query.queryFirstName || '',
      queryLastName: route.query.queryLastName || '',
      querySurname: route.query.querySurname || '',
      queryPhoneNumber: route.query.queryPhoneNumber || '',
      queryINN: route.query.queryINN || '',
      queryLicense: route.query.queryLicense || '',
      INNStatus: ['0', '-1'].includes(route.query.INNStatus) ? Number(route.query.INNStatus) : 1,
      passportStatus: ['0', '-1'].includes(route.query.passportStatus) ? Number(route.query.passportStatus) : 1,
    })

    const statusINNOptions = ref([
      { text: 'Все записи', value: 1 },
      { text: 'Проверено', value: 0 },
      { text: 'Не проверено', value: -1 },
    ])

    const statusPassportOptions = ref([
      { text: 'Все записи', value: 1 },
      { text: 'Проверено', value: 0 },
      { text: 'Не проверено', value: -1 },
    ])

    const resetFilters = () => {
      filters.query = ''
      filters.queryFirstName = ''
      filters.queryLastName = ''
      filters.querySurname = ''
      filters.queryPhoneNumber = ''
      filters.queryINN = ''
      filters.queryLicense = ''
      filters.INNStatus = 1
      filters.passportStatus = 1
      fetchFirstPage()
    }

    const filterParams = computed(() => {
      const params = {}

      if (filters.query) {
        params.search = filters.query.toLowerCase()
      }
      if (filters.queryFirstName) {
        params.first_name_contains = filters.queryFirstName.toLowerCase()
      }
      if (filters.queryLastName) {
        params.last_name_contains = filters.queryLastName.toLowerCase()
      }
      if (filters.querySurname) {
        params.sur_name_contains = filters.querySurname.toLowerCase()
      }
      if (filters.queryPhoneNumber) {
        params.phone_contains = filters.queryPhoneNumber.toLowerCase()
      }
      if (filters.queryINN) {
        params.inn = filters.queryINN.toLowerCase()
      }
      if (filters.queryLicense) {
        params.license = filters.queryLicense.toLowerCase()
      }

      if (filters.INNStatus === 0) {
        params.dadata_is_end = true
      } else if (filters.INNStatus === -1) {
        params.dadata_is_end = false
      }

      if (filters.passportStatus === 0) {
        params.nalog_is_end = true
      } else if (filters.passportStatus === -1) {
        params.nalog_is_end = false
      }

      return params
    })

    onMounted(() => {
      const pageFromUrl = Number(route.query.page) || 1
      loadData(pageFromUrl)
    })

    function pageChanged(page) {
      loadData(page)
    }

    const updateURL = () => {
      router.replace({ query: { ...filters } }).catch(() => {})
    }

    const fetchFirstPage = () => {
      loadData(1)
      updateURL()
    }

    const debouncedFetchFirstPage = useDebounceFn(fetchFirstPage, 300)

    return {
      router,
      drivers,
      filters,
      filterParams,
      statusINNOptions,
      statusPassportOptions,
      isLoading,
      totalPages,
      currentPage,
      debouncedFetchFirstPage,
      updateURL,
      loadData,
      pageChanged,
      resetFilters,
    }
  },
}
</script>

<style scoped>
.btn {
  border: 1px solid #d7dce2;
  background: #fff;
  color: #333;
  cursor: pointer;
  font-size: 13px;
  height: 32px;
  border-radius: 4px;
  padding: 0 12px;
}

.btn:hover {
  border-color: #007bff;
  color: #007bff;
}

.btn-primary {
  background: #007bff;
  border-color: #007bff;
  color: #fff;
}

.btn-primary:hover {
  background: #0056b3;
  border-color: #0056b3;
  color: #fff;
}

.filters-actions {
  display: flex;
  align-items: flex-end;
}

.content {
  display: flex;
  flex-direction: column;
  font-size: 12px;
  width: 100%;
}

.state-block {
  padding: 32px;
  color: #667085;
  font-size: 14px;
}

.driver-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  font-size: 13px;
}

.driver-table th,
.driver-table td {
  height: 32px;
  padding: 6px 8px;
  border-bottom: 1px solid #edf0f3;
  text-align: left;
  vertical-align: middle;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.driver-table th {
  position: sticky;
  top: 0;
  z-index: 1;
  background: #f7f8fa;
  color: #444;
  font-weight: bold;
}

.driver-table td {
  font-weight: bold;
}

.row-card {
  cursor: pointer;
}

.row-card:hover {
  background-color: rgba(224, 187, 72, 0.4);
}

.status-badge {
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  border-radius: 10px;
  padding: 2px 7px;
  font-size: 12px;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
}

.st-verified {
  background: #e6f4ea;
  color: #1e7e34;
}

.st-nverified {
  background: #fdeaea;
  color: #c0392b;
}
</style>
